import { useSyncExternalStore } from 'react';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
  platformRole: 'super_admin' | 'support_admin' | null;
  emailVerified: boolean;
  createdAt: string;
}

export interface AuthOrganisation {
  id: string;
  name: string;
  slug: string;
  roleKeys: string[];
  permissions: string[];
  onboardingStep: number;
}

export type AuthStatus = 'unknown' | 'authenticated' | 'anonymous';

export interface AuthState {
  status: AuthStatus;
  /** Held in memory only. Never persisted — a refresh cookie rebuilds it. */
  accessToken: string | null;
  user: AuthUser | null;
  organisations: AuthOrganisation[];
}

export interface Session {
  accessToken: string;
  user: AuthUser;
  organisations: AuthOrganisation[];
}

const LAST_ORG_KEY = 'siteflow.lastOrg';

const INITIAL: AuthState = {
  status: 'unknown',
  accessToken: null,
  user: null,
  organisations: [],
};

let state: AuthState = INITIAL;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function setState(next: AuthState): void {
  state = next;
  emit();
}

export function getAuthState(): AuthState {
  return state;
}

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setSession(session: Session): void {
  setState({
    status: 'authenticated',
    accessToken: session.accessToken,
    user: session.user,
    organisations: session.organisations,
  });
}

/** Rotates only the token — used by the silent refresh path. */
export function setAccessToken(accessToken: string): void {
  setState({ ...state, status: 'authenticated', accessToken });
}

export function setProfile(user: AuthUser, organisations: AuthOrganisation[]): void {
  setState({ ...state, status: 'authenticated', user, organisations });
}

export function clearSession(): void {
  setState({ status: 'anonymous', accessToken: null, user: null, organisations: [] });
}

/** Used on boot when the silent refresh comes back empty. */
export function markAnonymous(): void {
  if (state.status === 'anonymous' && state.accessToken === null) return;
  clearSession();
}

/** Test-only reset back to the pre-boot state. */
export function resetAuthStore(): void {
  setState(INITIAL);
}

export function getLastOrgId(): string | null {
  try {
    return localStorage.getItem(LAST_ORG_KEY);
  } catch {
    return null;
  }
}

export function setLastOrgId(orgId: string): void {
  try {
    localStorage.setItem(LAST_ORG_KEY, orgId);
  } catch {
    /* private mode — last-used org simply is not remembered */
  }
}

export function clearLastOrgId(): void {
  try {
    localStorage.removeItem(LAST_ORG_KEY);
  } catch {
    /* ignore */
  }
}

export function useAuth(): AuthState {
  return useSyncExternalStore(subscribeAuth, getAuthState, getAuthState);
}

export function useOrganisation(orgId: string | null | undefined): AuthOrganisation | null {
  const { organisations } = useAuth();
  if (!orgId) return null;
  return organisations.find((org) => org.id === orgId) ?? null;
}
